import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import ParallaxTiltCard from '../../components/common/ParallaxTiltCard';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { getMemberList } from '../../api/member';
import { getNoticeList } from '../../api/notice';
import { getLoginHistoryList } from '../../api/history';
import { getTopStoresToday, getTableOrderList, getStoreDashboardSummary } from '../../api/tableOrder';
import { formatDateTime } from '../../utils/format';

const STORE_ADMIN_ROLE = 'STORE_ADMIN';

export default function DashboardPage() {
  const { auth } = useAuth();
  const isStoreAdmin = auth?.roleCode === STORE_ADMIN_ROLE;

  const [stats, setStats] = useState({ memberCount: null, noticeCount: null, todayLogin: null });
  const [storeSummary, setStoreSummary] = useState(null);
  const [topStores, setTopStores] = useState([]);
  const [topStoresUpdatedAt, setTopStoresUpdatedAt] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentOrdersUpdatedAt, setRecentOrdersUpdatedAt] = useState(null);

  // 매장관리자는 회원수/공지사항/관리자 로그인이력에 접근 권한이 없고(SecurityConfig 참고)
  // KPI 카드와 하단 위젯 모두 매장 주문 통계로 대체되므로, 아예 다른 API 를 호출한다.
  useEffect(() => {
    if (isStoreAdmin) {
      getStoreDashboardSummary()
        .then((res) => setStoreSummary(res.data))
        .catch(() => setStoreSummary({ monthlyOrderCount: 0, todayOrderCount: 0, todayOrderAmount: 0, topProducts: [] }));
      return;
    }
    (async () => {
      try {
        const [members, notices, logins] = await Promise.all([
          getMemberList({ pageNo: 1, pageSize: 1 }),
          getNoticeList({ pageNo: 1, pageSize: 1 }),
          getLoginHistoryList({ pageNo: 1, pageSize: 5 }),
        ]);
        setStats({
          memberCount: members.data?.totalCount ?? 0,
          noticeCount: notices.data?.totalCount ?? 0,
          recentLogins: logins.data?.list ?? [],
        });
      } catch {
        // 대시보드는 통계 조회 실패 시에도 화면 자체는 보여준다.
        setStats({ memberCount: 0, noticeCount: 0, recentLogins: [] });
      }
    })();
  }, [isStoreAdmin]);

  // 오늘 테이블오더 상위 매장 - 매장관리자는 배정된 매장 하나(또는 소수)만 보이는 "순위"라
  // 의미가 없으므로, 이 위젯은 SUPER_ADMIN/ADMIN 에게만 보여준다 (실시간 반영 15초 주기).
  const fetchTopStores = useCallback(async () => {
    try {
      const res = await getTopStoresToday(3);
      setTopStores(res.data || []);
      setTopStoresUpdatedAt(new Date());
    } catch {
      // 조회 실패 시 기존 값을 유지한다.
    }
  }, []);

  useEffect(() => {
    if (isStoreAdmin) return;
    fetchTopStores();
    const interval = setInterval(fetchTopStores, 15000);
    return () => clearInterval(interval);
  }, [isStoreAdmin, fetchTopStores]);

  // 매장관리자 전용 - 위 위젯 대신 본인 매장의 테이블오더 현황을 보여준다.
  // 목록 조회는 StoreAccessGuard 에 의해 서버에서 이미 배정 매장으로 제한된다.
  const fetchRecentOrders = useCallback(async () => {
    try {
      const res = await getTableOrderList({ pageNo: 1, pageSize: 5 });
      setRecentOrders(res.data?.list || []);
      setRecentOrdersUpdatedAt(new Date());
    } catch {
      // 조회 실패 시 기존 값을 유지한다.
    }
  }, []);

  useEffect(() => {
    if (!isStoreAdmin) return;
    fetchRecentOrders();
    const interval = setInterval(fetchRecentOrders, 15000);
    return () => clearInterval(interval);
  }, [isStoreAdmin, fetchRecentOrders]);

  return (
    <AdminLayout title="대시보드" menuUrl="/admin/dashboard">
      <div className="parallax-hero">
        <div className="page-header">
          <div>
            <h1>대시보드</h1>
            <div className="subtitle">onetak 관리자 사이트 현황</div>
          </div>
        </div>
      </div>

      {isStoreAdmin ? (
        <div className="kpi-grid">
          <ParallaxTiltCard className="kpi-card emphasis">
            <div className="kpi-label">이번 달 전체 주문수</div>
            <div className="kpi-value">{storeSummary?.monthlyOrderCount?.toLocaleString() ?? '—'}<span className="unit">건</span></div>
          </ParallaxTiltCard>
          <ParallaxTiltCard className="kpi-card">
            <div className="kpi-label">오늘 주문수</div>
            <div className="kpi-value">{storeSummary?.todayOrderCount?.toLocaleString() ?? '—'}<span className="unit">건</span></div>
          </ParallaxTiltCard>
          <ParallaxTiltCard className="kpi-card">
            <div className="kpi-label">오늘 주문금액</div>
            <div className="kpi-value">{storeSummary?.todayOrderAmount?.toLocaleString() ?? '—'}<span className="unit">원</span></div>
          </ParallaxTiltCard>
        </div>
      ) : (
        <div className="kpi-grid">
          <ParallaxTiltCard className="kpi-card emphasis">
            <div className="kpi-label">전체 회원 수</div>
            <div className="kpi-value">{stats.memberCount ?? '—'}<span className="unit">명</span></div>
          </ParallaxTiltCard>
          <ParallaxTiltCard className="kpi-card">
            <div className="kpi-label">공지사항</div>
            <div className="kpi-value">{stats.noticeCount ?? '—'}<span className="unit">건</span></div>
          </ParallaxTiltCard>
          <ParallaxTiltCard className="kpi-card">
            <div className="kpi-label">최근 로그인 기록</div>
            <div className="kpi-value">{stats.recentLogins?.length ?? '—'}<span className="unit">건</span></div>
          </ParallaxTiltCard>
        </div>
      )}

      {isStoreAdmin ? (
        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <div className="flex-between" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15 }}>테이블오더 현황 <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-secondary)' }}>(최근 5건)</span></h2>
            <span className="dashboard-live">
              <span className="dashboard-live-dot" />
              실시간
              {recentOrdersUpdatedAt && <span className="dashboard-live-time"> · {formatDateTime(recentOrdersUpdatedAt)} 기준</span>}
            </span>
          </div>

          {recentOrders.length === 0 ? (
            <div className="empty-row" style={{ padding: '28px 16px' }}>접수된 테이블오더가 없습니다.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>주문번호</th>
                    <th>테이블</th>
                    <th>금액</th>
                    <th>상태</th>
                    <th>접수일시</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((o) => (
                    <tr key={o.tableOrderId}>
                      <td>{o.tableOrderId}</td>
                      <td>{o.tableNo}</td>
                      <td>{Number(o.totalAmount).toLocaleString()}원</td>
                      <td><StatusBadge value={o.orderStatus} /></td>
                      <td>{formatDateTime(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Link to="/table-orders" className="btn btn-secondary btn-sm">테이블오더관리 전체보기 →</Link>
          </div>
        </div>
      ) : (
        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <div className="flex-between" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15 }}>오늘 테이블오더 상위 매장 <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-secondary)' }}>(주문수 기준 TOP 3)</span></h2>
            <span className="dashboard-live">
              <span className="dashboard-live-dot" />
              실시간
              {topStoresUpdatedAt && <span className="dashboard-live-time"> · {formatDateTime(topStoresUpdatedAt)} 기준</span>}
            </span>
          </div>

          {topStores.length === 0 ? (
            <div className="empty-row" style={{ padding: '28px 16px' }}>오늘 접수된 테이블오더가 없습니다.</div>
          ) : (
            <div className="top-store-grid">
              {topStores.map((s, idx) => (
                <div className={`top-store-card${idx === 0 ? ' rank-first' : ''}`} key={s.storeId}>
                  <div className="top-store-rank">{idx + 1}</div>
                  <div className="top-store-body">
                    <div className="top-store-name">{s.storeName}</div>
                    <div className="top-store-metrics">
                      <div className="top-store-metric">
                        <span className="top-store-metric-value">{Number(s.orderCount).toLocaleString()}</span>
                        <span className="top-store-metric-unit">건</span>
                      </div>
                      <div className="top-store-metric">
                        <span className="top-store-metric-value">{Number(s.totalAmount).toLocaleString()}</span>
                        <span className="top-store-metric-unit">원</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isStoreAdmin ? (
        <div className="card card-padded">
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>이번 주 최다 판매 상품 <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-secondary)' }}>(TOP 10, 주문수 1순위 · 주문금액 2순위)</span></h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>순위</th>
                  <th>상품명</th>
                  <th>주문수</th>
                  <th>주문금액</th>
                </tr>
              </thead>
              <tbody>
                {(!storeSummary?.topProducts || storeSummary.topProducts.length === 0) && (
                  <tr><td colSpan={4} className="empty-row">이번 주 판매된 상품이 없습니다.</td></tr>
                )}
                {storeSummary?.topProducts?.map((p, idx) => (
                  <tr key={p.productId}>
                    <td>{idx + 1}</td>
                    <td>{p.productName}</td>
                    <td>{Number(p.orderQty).toLocaleString()}건</td>
                    <td>{Number(p.orderAmount).toLocaleString()}원</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card card-padded">
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>최근 관리자 로그인 이력</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>계정</th>
                  <th>결과</th>
                  <th>접속 IP</th>
                  <th>일시</th>
                </tr>
              </thead>
              <tbody>
                {(!stats.recentLogins || stats.recentLogins.length === 0) && (
                  <tr><td colSpan={4} className="empty-row">로그인 이력이 없습니다.</td></tr>
                )}
                {stats.recentLogins?.map((h) => (
                  <tr key={h.loginHistId}>
                    <td>{h.adminAccount}</td>
                    <td>{h.loginResult}</td>
                    <td>{h.loginIp}</td>
                    <td>{formatDateTime(h.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
